/**
 * NetworkSelector - Admin-only dropdown for switching between network tenants
 * Replaces legacy Vue.js network-tenant-ddl widget
 * Renders only for Root/NetOps/TechOps users; nothing for other users
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Command,
  CommandInput as _CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  cn,
} from '@/components/ui';

import { Check, ChevronsUpDown } from 'lucide-react';
import { useHasAnyRole } from '../UserContextProvider/use-has-role';
import { useTenant } from '../UserContextProvider/UserContextProvider.store';
import { useNetworkSelector } from './network-selector.hook';

// CommandInputProps omits standard HTML input attrs (placeholder, className) because cmdk
// is a devDep of the package and its types aren't available here. Cast to restore them.
const CommandInput = _CommandInput as React.ComponentType<{
  placeholder?: string;
  className?: string;
  hasIcon?: boolean;
}>;

// Admin roles that can see the network selector (matches LDAdmin policy)
const ADMIN_ROLES = ['Root', 'NetOps', 'TechOps'] as const;

export interface NetworkSelectorProps {
  /** Optional className for outer container */
  className?: string;
}

/**
 * NetworkSelector - Renders admin dropdown only.
 * Non-admin users see nothing (component returns null).
 */
export function NetworkSelector({ className }: NetworkSelectorProps) {
  const isAdmin = useHasAnyRole(...ADMIN_ROLES);

  if (!isAdmin) {
    return null;
  }

  return <AdminNetworkSelector className={className} />;
}

/**
 * AdminNetworkSelector - Internal component, only rendered for admin users
 * Separated to avoid hook calls when user is not admin
 */
function AdminNetworkSelector({ className }: { className?: string }) {
  const { t } = useTranslation(NS.COMMON);
  const tenant = useTenant();
  const { tenants, hostDomain, loading, error } = useNetworkSelector();
  const [open, setOpen] = useState(false);
  const [navigating, setNavigating] = useState(false);

  const currentTenantId = tenant?.id ?? '';

  // Navigate immediately to the chosen tenant's subdomain (cross-subdomain).
  const navigateToTenant = useCallback(
    (tenantId: string) => {
      if (!tenantId || navigating) return;
      const target = tenants.find((tn) => tn.id === tenantId);
      if (!target) return;

      setNavigating(true);
      window.location.href = `https://${target.tenant}.${hostDomain}/network-visit`;
    },
    [tenants, hostDomain, navigating],
  );

  // Case-insensitive substring filter using the label text (passed via CommandItem keywords).
  // Overrides cmdk's default fuzzy/subsequence algorithm which produces false positives.
  // MUST be declared before early returns to satisfy Rules of Hooks.
  const networkFilter = useCallback(
    (_value: string, search: string, keywords?: string[]) => {
      const label = keywords?.[0] ?? _value;
      return label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
    },
    [],
  );

  if (loading) {
    return (
      <div className={cn('flex items-center', className)}>
        <div className="h-8 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  // Error or no tenants — render nothing in sidebar (avoid orphan static text)
  if (error || tenants.length === 0) {
    return null;
  }

  const getLabel = (tn: { title: string; networkId: number; tenant: string }) =>
    `${tn.title} (${tn.networkId} - ${tn.tenant})`;

  const currentTenantItem = tenants.find((tn) => tn.id === currentTenantId);
  const currentLabel = currentTenantItem ? getLabel(currentTenantItem) : '';

  const triggerText = navigating
    ? t('NetworkSelector_Loading', { defaultValue: 'Loading…' })
    : currentLabel || t('NetworkSelector_Placeholder', { defaultValue: 'Select network tenant' });

  return (
    <div className={cn('w-full min-w-0', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={navigating}
            title={currentLabel || undefined}
            className={cn(
              'h-8 w-full max-w-full min-w-0 justify-between text-xs font-medium',
              !currentLabel && 'text-muted-foreground',
            )}
          >
            <span className="truncate min-w-0">{triggerText}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
          <Command filter={networkFilter}>
            <CommandInput
              placeholder={t('NetworkSelector_SearchPlaceholder', {
                defaultValue: 'Search networks...',
              })}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {t('NetworkSelector_EmptyText', { defaultValue: 'No networks found' })}
              </CommandEmpty>
              <CommandGroup>
                {tenants.map((tn) => (
                  <CommandItem
                    key={tn.id}
                    value={tn.id}
                    keywords={[getLabel(tn)]}
                    onSelect={(value) => {
                      setOpen(false);
                      if (value === currentTenantId) return;
                      navigateToTenant(value);
                    }}
                    className="items-start whitespace-normal break-words"
                  >
                    <Check
                      className={cn(
                        'mr-2 mt-0.5 h-4 w-4 shrink-0',
                        currentTenantId === tn.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="flex-1 break-words">{getLabel(tn)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
